/**
 * LayersControl.tsx — Map layer toggles with frosted-glass surface.
 *
 * Styled after a reference LayerTogglePanel: glass surface (backdrop blur + semi-transparent),
 * grouped with small uppercase section label, each row has a color swatch + label +
 * sliding toggle switch. Uses brand teal (--primary) for the 'on' state.
 *
 * Accessible: each row is a <button> with aria-pressed; the toggle switch is
 * purely visual (not a hidden checkbox) so screen readers see the pressed state.
 */
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CATEGORY_COLORS } from './map/POILayerMgr'
import { TractFilterPanel } from './map/TractFilterPanel'
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover'

interface LayersControlProps {
  showTracts: boolean
  showEVChargers: boolean
  showPOI: boolean
  showRoads: boolean
  /** T-508: the T-504 site-vicinity micro-layer (blocks + block groups around a searched site). */
  showVicinity: boolean
  onToggleTracts: () => void
  onToggleEV: () => void
  onTogglePOI: () => void
  onToggleRoads: () => void
  onToggleVicinity: () => void
  // T-114: POI is zoom-gated. When the map is below POI_MIN_ZOOM the toggle is disabled
  // (a fresh Overpass query at low-zoom radii 502s), with a "zoom in" hint. Defaults to false.
  poiDisabled?: boolean
  /**
   * T-426: true at zooms where the tract layer is not on screen (county/state choropleth), so
   * the tract tier/type filter has no layer to act on. Greys the filter out and says why —
   * before this, clicks at those zooms were accepted and silently discarded (Noreen, 2026-07-22).
   */
  tractFilterDisabled?: boolean
  // T-012/W3: POI category filter (sub-panel shown when showPOI is true)
  enabledPOICategories: string[]
  onTogglePOICategory: (category: string) => void
  onSelectAllPOICategories: () => void
  onClearPOICategories: () => void
  // T-012/W4: tract tier/type filter (popover opened from the Score Tracts row chevron)
  tractTierFilter: string[]
  tractTypeFilter: string[]
  onTractTierToggle: (tier: string) => void
  onTractTypeToggle: (type: string) => void
}

const POI_CATEGORY_LIST = Object.keys(CATEGORY_COLORS)

interface LayerRow {
  id: 'tracts' | 'ev' | 'poi' | 'roads' | 'vicinity'
  label: string
  swatchColor: string
}

const LAYERS: LayerRow[] = [
  { id: 'tracts', label: 'Score Tracts', swatchColor: '#15803d' }, // Prime-tier green
  // T-508: sits beside Score Tracts because it is the same proprietary score data one tier
  // finer — census blocks around the searched site. The swatch borrows the block-GROUP
  // backdrop purple rather than a block hue: blocks are drawn two-hued (Corridor orange,
  // Retail/Leisure green) and no single swatch can honestly stand for both, while orange and
  // green are already spoken for by Major roads and Score Tracts.
  // T-628: this row was hidden by T-519 while the layer was uninterpretable colour — there was
  // no block-level number anywhere in the UI to read it against. T-512 shipped the block
  // classification readout and T-628 puts block scores on the site panel, so the condition
  // T-519 named for restoring it is met and the row is back.
  { id: 'vicinity', label: 'Block detail', swatchColor: '#7c3aed' },
  { id: 'ev', label: 'EV Chargers', swatchColor: '#f97316' }, // --score-low orange
  { id: 'poi', label: 'Points of Interest', swatchColor: '#6366f1' }, // indigo
  // T-428: "Major roads", not "Highways" — the layer carries interstates and US routes plus other
  // roads at 10k+ AADT, so the toggle names what it delivers and matches the legend's vocabulary.
  { id: 'roads', label: 'Major roads', swatchColor: '#E67E22' }, // Interstate orange (§11)
]

interface ToggleSwitchProps {
  on: boolean
}

function ToggleSwitch({ on }: ToggleSwitchProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative flex h-[14px] w-[26px] shrink-0 items-center rounded-full border transition-colors duration-150',
        on ? 'border-primary bg-primary/20' : 'border-border bg-muted',
      )}
    >
      <span
        className={cn(
          'absolute h-[10px] w-[10px] rounded-full transition-all duration-150',
          on ? 'left-[13px] bg-primary' : 'left-[1px] bg-muted-foreground/60',
        )}
      />
    </span>
  )
}

export function LayersControl({
  showTracts,
  showEVChargers,
  showPOI,
  showRoads,
  showVicinity,
  onToggleTracts,
  onToggleEV,
  onTogglePOI,
  onToggleRoads,
  onToggleVicinity,
  poiDisabled = false,
  tractFilterDisabled = false,
  enabledPOICategories,
  onTogglePOICategory,
  onSelectAllPOICategories,
  onClearPOICategories,
  tractTierFilter,
  tractTypeFilter,
  onTractTierToggle,
  onTractTypeToggle,
}: LayersControlProps) {
  const isOn = {
    tracts: showTracts,
    ev: showEVChargers,
    poi: showPOI,
    roads: showRoads,
    vicinity: showVicinity,
  }
  const onToggle = {
    tracts: onToggleTracts,
    ev: onToggleEV,
    poi: onTogglePOI,
    roads: onToggleRoads,
    vicinity: onToggleVicinity,
  }

  // T-339: the POI category list is collapsible. It stays collapsed by default so the panel
  // doesn't grow tall enough to cover the search bar on small viewports; the user expands it
  // via the "View / Filter Categories" button to pick categories.
  const [categoriesOpen, setCategoriesOpen] = useState(false)

  return (
    <div className="glass rounded-xl border border-border/60 shadow-md p-3 w-48">
      {/* Section label — uppercase, dim */}
      <p className="mb-2 text-[0.625rem] font-bold uppercase tracking-widest text-muted-foreground">
        Layers
      </p>

      <div className="space-y-0.5">
        {LAYERS.map((layer) => {
          const on = isOn[layer.id]
          // T-114: the POI row is disabled below the zoom threshold.
          const rowDisabled = layer.id === 'poi' && poiDisabled
          return (
            <div key={layer.id}>
              <div className="flex items-center">
                <button
                  onClick={onToggle[layer.id]}
                  disabled={rowDisabled}
                  aria-pressed={on}
                  title={rowDisabled ? 'Zoom in to see points of interest' : undefined}
                  className={cn(
                    `flex flex-1 items-center gap-2 rounded-md px-1.5 py-1.5
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                     min-h-[36px] transition-colors text-left`,
                    rowDisabled
                      ? 'cursor-not-allowed opacity-40'
                      : 'hover:bg-black/5 dark:hover:bg-white/5',
                  )}
                >
                  {/* Color swatch */}
                  <span
                    className={cn(
                      'h-2.5 w-2.5 shrink-0 rounded-full transition-opacity',
                      !on && 'opacity-25',
                    )}
                    style={{ backgroundColor: layer.swatchColor }}
                    aria-hidden="true"
                  />
                  {/* Label */}
                  <span
                    className={cn(
                      'flex-1 text-xs transition-colors',
                      on ? 'text-foreground' : 'text-muted-foreground/60',
                    )}
                  >
                    {layer.label}
                  </span>
                  {/* Toggle switch */}
                  <ToggleSwitch on={on} />
                </button>

                {/* T-012/W4: tract tier/type filter popover — chevron beside the Tracts row */}
                {layer.id === 'tracts' && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        aria-label="Filter tracts"
                        className={cn(
                          'ml-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md',
                          'text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors',
                          !on && 'opacity-40',
                        )}
                      >
                        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      side="right"
                      sideOffset={8}
                      className="w-60 glass border-border/60"
                    >
                      <p className="mb-2 text-[0.625rem] font-bold uppercase tracking-widest text-muted-foreground">
                        Filter Tracts
                      </p>
                      <TractFilterPanel
                        tierFilter={tractTierFilter}
                        typeFilter={tractTypeFilter}
                        onTierToggle={onTractTierToggle}
                        onTypeToggle={onTractTypeToggle}
                        disabled={tractFilterDisabled}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>

              {/* T-426: zoom hint when the tract filter cannot act at the current zoom. Sits
                  under the Tracts row like the POI hint, so it is visible WITHOUT opening the
                  popover — the user who just clicked a filter and saw nothing happen is the one
                  who needs it. */}
              {layer.id === 'tracts' && tractFilterDisabled && (
                <p className="ml-3 mt-0.5 pl-2 text-[0.625rem] leading-tight text-muted-foreground/70">
                  Zoom in to filter tracts
                </p>
              )}

              {/* T-114: zoom hint when POI is gated out at the current zoom. */}
              {layer.id === 'poi' && poiDisabled && (
                <p className="ml-3 mt-0.5 pl-2 text-[0.625rem] leading-tight text-muted-foreground/70">
                  Zoom in to see points of interest
                </p>
              )}

              {/* POI category sub-panel — only when the POI layer is on AND zoom allows it.
                  T-339: collapsed behind a "View / Filter Categories" button by default so the
                  12-row list can't cover the search bar on small viewports. */}
              {layer.id === 'poi' && showPOI && !poiDisabled && (
                <div className="mt-0.5 ml-3 border-l border-border/50 pl-2">
                  <button
                    onClick={() => setCategoriesOpen((open) => !open)}
                    aria-expanded={categoriesOpen}
                    className="flex w-full items-center justify-between gap-1 rounded px-1 py-0.5
                               text-[0.5625rem] font-semibold uppercase tracking-wider text-muted-foreground
                               hover:bg-black/5 dark:hover:bg-white/5
                               focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring
                               transition-colors"
                  >
                    <span>View / Filter Categories</span>
                    <ChevronDown
                      className={cn(
                        'h-3 w-3 shrink-0 transition-transform',
                        categoriesOpen && 'rotate-180',
                      )}
                      aria-hidden="true"
                    />
                  </button>

                  {categoriesOpen && (
                    <div className="mt-1">
                      <div className="mb-1 flex items-center justify-end">
                        <span className="flex gap-1.5 text-[0.625rem]">
                          <button
                            onClick={onSelectAllPOICategories}
                            className="text-primary hover:underline focus-visible:outline-none"
                          >
                            All
                          </button>
                          <button
                            onClick={onClearPOICategories}
                            className="text-muted-foreground hover:underline focus-visible:outline-none"
                          >
                            None
                          </button>
                        </span>
                      </div>
                      <div className="space-y-0">
                        {POI_CATEGORY_LIST.map((cat) => {
                          const catOn = enabledPOICategories.includes(cat)
                          return (
                            <button
                              key={cat}
                              onClick={() => onTogglePOICategory(cat)}
                              aria-pressed={catOn}
                              className="flex w-full items-center gap-1.5 rounded px-1 py-0.5
                                     hover:bg-black/5 dark:hover:bg-white/5
                                     focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring
                                     transition-colors text-left"
                            >
                              <span
                                className={cn(
                                  'h-2 w-2 shrink-0 rounded-full transition-opacity',
                                  !catOn && 'opacity-25',
                                )}
                                style={{ backgroundColor: CATEGORY_COLORS[cat] }}
                                aria-hidden="true"
                              />
                              <span
                                className={cn(
                                  'flex-1 text-[0.6875rem] transition-colors',
                                  catOn ? 'text-foreground' : 'text-muted-foreground/60',
                                )}
                              >
                                {cat}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
